import { Box, FormControl, Tabs, Tab } from "@mui/material";
import React from "react";

import { COLORS } from "../assets/theme";
import CustomTextField from "../components/CustomTextField";
import CustomButton from "../components/CustomButton";
import CustomSelect from "../components/CustomSelect";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import CustomTable from "../components/table/CustomTable";
import {
  sendAllNotification,
  sendNotificationToVenue,
  sendNotificationToOrganization,
  sendNotificationToClient,
  getAllNotifications,
} from "../services/NotificationService";
import { getUserId } from "../services/authService";
import { toast } from "react-toastify";
import { getAllBranches } from "../services/BranchesService";
import { getAllClients } from "../services/ClientsService";
import { getAllCommunity } from "../services/CommunityService";
import { useLocation, useParams } from "react-router-dom";
import { getAllVenues } from "../services/VenueService";

const UserNotifications: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const venueId = location.state?.venueId || params.branchId || null;
  const [tab, setTab] = React.useState<number>(0);
  const [search, setSearch] = React.useState<string>("");
  const [title, setTitle] = React.useState<string>("");
  const [message, setMessage] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = React.useState<
    string | number | (string | number)[]
  >("");
  const [selectedTarget, setSelectedTarget] = React.useState<
    string | number | (string | number)[]
  >("");
  const [selectedAudience, setSelectedAudience] = React.useState<
    (string | number)[]
  >([]);
  const [audienceOptions, setAudienceOptions] = React.useState<any[]>([]);
  const [loadingAudience, setLoadingAudience] = React.useState(false);
  const [rows, setRows] = React.useState(() => {
    const total = 10;
    return Array.from({ length: total }, (_, i) => {
      const id = i + 1;
      const to = `Staff ${id}`;
      const message = `hii staff welcome here`;
      const sentDate = "23/8/2008";
      return { id, to, message, sentDate };
    });
  });
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = React.useState(false);
  const [branches, setBranches] = React.useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [venues, setVenues] = React.useState<any[]>([]);
  const [loadingVenues, setLoadingVenues] = React.useState(false);
  const [selectedVenue, setSelectedVenue] = React.useState<
    string | number | (string | number)[]
  >("");

  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Please fill in both title and message.");
      return;
    }

    if (tab === 2) {
      if (!selectedVenue) {
        toast.error("Please select a venue.");
        return;
      }
    } else if (tab === 0 || tab === 1) {
      if (!selectedBranch) {
        toast.error("Please select a branch.");
        return;
      }
    }
    if (tab === 0) {
      if (!selectedTarget) {
        toast.error("Please select a target.");
        return;
      }

      if (!selectedAudience || selectedAudience.length === 0) {
        toast.error("Please select at least one audience.");
        return;
      }
    }

    const sender_id = getUserId();
    if (!sender_id) {
      toast.error("User not authenticated. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        message,
        body: message,
        sender_id,
        title,
      };

      if (tab === 2) {
        payload.venue_id = selectedVenue;
      } else if (tab === 0 || tab === 1) {
        payload.business_id = selectedBranch;
      } else {
        payload.admin_user_id = sender_id;
      }
      if (tab === 0 && selectedTarget && selectedAudience.length > 0) {
        if (selectedTarget === "individualclient") {
          payload.client_ids = selectedAudience;
        } else if (selectedTarget === "communitygroups") {
          payload.group_id = selectedAudience[0];
        }
      }

      if (tab === 0) {
        await sendNotificationToClient(payload);
      } else if (tab === 1) {
        await sendAllNotification(payload);
      } else if (tab === 2) {
        await sendNotificationToVenue(payload);
      } else {
        await sendNotificationToOrganization(payload);
      }
      toast.success("Notification sent successfully!");
      fetchNotifications();
      setTitle("");
      setMessage("");
      setSelectedAudience([]);
    } catch (error: any) {
      toast.error("Failed to send notification. Please try again.");
      console.error("Send notification error:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        if (!venueId) {
          console.error("No venue ID available");
          setBranches([]);
          return;
        }

        const response = await getAllBranches(venueId);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, [venueId]);

  React.useEffect(() => {
    const fetchVenues = async () => {
      setLoadingVenues(true);
      try {
        const response = await getAllVenues();
        const venuesData = Array.isArray(response)
          ? response
          : Array.isArray((response as any)?.venues)
          ? (response as any).venues
          : Array.isArray((response as any)?.data)
          ? (response as any).data
          : [];

        setVenues(venuesData);
      } catch (error) {
        console.error("Error fetching venues:", error);
        setVenues([]);
        toast.error("Failed to fetch venues.");
      } finally {
        setLoadingVenues(false);
      }
    };

    fetchVenues();
  }, []);

  React.useEffect(() => {
    const fetchAudience = async () => {
      if (!selectedBranch || !selectedTarget) {
        setAudienceOptions([]);
        return;
      }

      setLoadingAudience(true);
      try {
        if (selectedTarget === "individualclient") {
          const response = await getAllClients(String(selectedBranch));

          let clientsData = [];
          if (response && response.users) {
            clientsData = response.users;
          } else if (response && response.clients) {
            clientsData = response.clients;
          } else if (response && response.data) {
            clientsData = response.data;
          } else if (Array.isArray(response)) {
            clientsData = response;
          }

          setAudienceOptions(Array.isArray(clientsData) ? clientsData : []);
        } else if (selectedTarget === "communitygroups") {
          const response = await getAllCommunity(String(selectedBranch));

          let communitiesData = [];
          if (response && response.groups) {
            communitiesData = response.groups;
          } else if (response && response.data) {
            communitiesData = response.data;
          } else if (Array.isArray(response)) {
            communitiesData = response;
          }
          setAudienceOptions(
            Array.isArray(communitiesData) ? communitiesData : []
          );
        }
      } catch (error) {
        console.error("Error fetching audience:", error);
        setAudienceOptions([]);
        toast.error("Failed to fetch audience options.");
      } finally {
        setLoadingAudience(false);
      }
    };

    fetchAudience();
  }, [selectedBranch, selectedTarget]);

  const fetchNotifications = React.useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const response = await getAllNotifications();
      let notificationsData = [];
      if (response && response.notifications) {
        notificationsData = response.notifications;
      } else if (response && response.data) {
        notificationsData = response.data;
      } else if (Array.isArray(response)) {
        notificationsData = response;
      }
      setNotifications(
        Array.isArray(notificationsData) ? notificationsData : []
      );
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
      toast.error("Failed to fetch notifications.");
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  React.useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Box sx={{ px: 2, pt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: { xs: "100%", sm: "100%" },
            p: { xs: 2, sm: 3 },
            pt: { xs: 4, sm: 5 },
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: COLORS.WhiteBackground,
            position: "relative",
          }}
        >
          <Box sx={{ mb: { xs: 3, sm: 2 }, mt: { xs: -6, sm: -4 } }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              aria-label="notification-tabs"
              sx={{
                "& .MuiTabs-flexContainer": {
                  justifyContent: { xs: "center", sm: "flex-start" },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: (t) => t.palette.primary.main,
                },
                borderRadius: "8px 8px 0 0",
                overflow: "hidden",
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Tab
                label="Specific User"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="All Users"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="All Users in Venue"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="All Users in Organization"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
            </Tabs>
          </Box>

          {tab === 2 ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Select Venue"
                    options={venues.map((venue) => ({
                      value: venue.id || venue._id,
                      label:
                        venue.name ||
                        venue.title ||
                        venue.fullName ||
                        `ID: ${venue.id || venue._id}`,
                    }))}
                    value={selectedVenue}
                    onChange={(value: string | number | (string | number)[]) =>
                      setSelectedVenue(value)
                    }
                    disabled={loadingVenues}
                  />
                </FormControl>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          ) : tab === 1 ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Select Branch"
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                    value={selectedBranch}
                    onChange={(value: string | number | (string | number)[]) =>
                      setSelectedBranch(value)
                    }
                    disabled={loadingBranches}
                  />
                </FormControl>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          ) : tab === 3 ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ mb: 1.5 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Select Branch"
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                    value={selectedBranch}
                    onChange={(value: string | number | (string | number)[]) =>
                      setSelectedBranch(value)
                    }
                    disabled={loadingBranches}
                  />
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gridTemplateColumns: { xs: "1fr", sm: " 1fr" },
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Target"
                    options={[
                      { value: "individualclient", label: "Individual Client" },
                      { value: "communitygroups", label: "Community Groups" },
                    ]}
                    value={selectedTarget}
                    onChange={(
                      value: string | number | (string | number)[]
                    ) => {
                      setSelectedTarget(value);
                      setSelectedAudience([]);
                    }}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    size="large"
                    searchable
                    multiSelect
                    label={
                      selectedTarget === "individualclient"
                        ? "Select Clients"
                        : selectedTarget === "communitygroups"
                        ? "Select Communities"
                        : "Select Audience"
                    }
                    options={audienceOptions.map((item) => ({
                      value: item.id || item._id,
                      label:
                        selectedTarget === "individualclient"
                          ? `${item.fullName || item.name || `ID: ${item.id || item._id}`}
                            (${item.phone || item.mobile || "No phone"})`
                          : item.name ||
                            item.title ||
                            item.fullName ||
                            `ID: ${item.id || item._id}`,
                    }))}
                    value={selectedAudience}
                    onChange={(
                      value: string | number | (string | number)[]
                    ) => {
                      console.log("Selected audience:", value);
                      setSelectedAudience(
                        Array.isArray(value) ? value : [value]
                      );
                    }}
                    disabled={loadingAudience || !selectedTarget}
                  />
                </FormControl>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ mt: { xs: 2, sm: 3 }, mb: 5 }}>
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loadingNotifications}
          fields={[
            { key: "title", header: "Title" },
            { key: "message", header: "Message" },
            { key: "created_at", header: "Created At" },
            { key: "recipient", header: "Recipient" },
          ]}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            let startDate: Date | null = null;
            let endDate: Date | null = null;
            try {
              if (dateRange) {
                const dr: any = dateRange;
                const s = Array.isArray(dr) ? dr[0] : dr.start ?? dr[0];
                const e = Array.isArray(dr) ? dr[1] : dr.end ?? dr[1];
                if (s) {
                  const sd = new Date(s);
                  if (!isNaN(sd.getTime())) startDate = sd;
                }
                if (e) {
                  const ed = new Date(e);
                  if (!isNaN(ed.getTime())) endDate = ed;
                }
              }
            } catch (err) {
              startDate = null;
              endDate = null;
            }

            const filtered = notifications.filter((r: any) => {
              if (startDate || endDate) {
                const created =
                  r && r.created_at ? new Date(r.created_at) : null;
                if (created && !isNaN(created.getTime())) {
                  if (startDate && created < startDate) return false;
                  if (endDate && created > endDate) return false;
                }
              }

              if (!normalizedQuery) return true;

              const haystack = [r.title, r.message, r.created_at, r.recipient]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
      </Box>
    </Box>
  );
};

export default UserNotifications;
